import { useToast } from '@foxtaibox/foxtailbox-frontend-core';
import {
  Interview_Reply_Event,
  useInterviewCurrentMessagesQuery,
  useInterviewReplySubscription,
} from '@generated/graphql';
import { Stack } from '@mui/material';
import { subscribeToWsReconnect } from 'config/apolloConfig';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { startQuestionTimer, stopQuestionTimer } from 'store/assessment/questionTimer';
import QuestionHeader from '../components/QuestionHeader';
import { QuestionPageContext } from '../context';
import Chat from './components/Chat';
import { ChatMessage } from './types';

interface InterviewQuestionProps {
  sessionId: string;
}

const REPLY_POLL_START_DELAY = 15_000;
const REPLY_POLL_INTERVAL = 5_000;

const InterviewQuestion = ({ sessionId }: InterviewQuestionProps) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<ChatMessage[] | null>(null);
  const [interviewReplyLoading, setInterviewReplyLoading] = useState(false);
  const messagesRef = useRef<ChatMessage[] | null>(null);
  const replyBaselineRef = useRef<number | null>(null);
  const finishedHandledRef = useRef(false);
  const { showSuccessToast } = useToast();

  const isInterviewFinished = messages?.length
    ? messages[messages.length - 1].event === Interview_Reply_Event.Finish
    : false;
  const messagesToPrint = isInterviewFinished ? messages?.slice(0, -1) : messages;

  const { handleFinishTesting } = useContext(QuestionPageContext);

  useEffect(() => {
    messagesRef.current = null;
    replyBaselineRef.current = null;
    finishedHandledRef.current = false;
    setMessages(null);
    setInterviewReplyLoading(false);
  }, [sessionId]);

  const { data: interviewReplySubscriptionData } = useInterviewReplySubscription({
    variables: { sessionId },
    fetchPolicy: 'no-cache',
  });

  const {
    data: interviewCurrentMessagesData,
    loading: interviewCurrentMessagesLoading,
    refetch: refetchInterviewCurrentMessages,
  } = useInterviewCurrentMessagesQuery({
    variables: { sessionId },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    const { interviewCurrentMessages } = interviewCurrentMessagesData || {};

    if (
      !interviewCurrentMessages ||
      (messagesRef.current && interviewCurrentMessages.length < messagesRef.current.length)
    ) {
      return;
    }

    messagesRef.current = interviewCurrentMessages;
    setMessages(interviewCurrentMessages);

    const lastMessage = interviewCurrentMessages[interviewCurrentMessages.length - 1];

    if (!lastMessage) {
      return;
    }

    if (lastMessage.event === Interview_Reply_Event.Finish) {
      replyBaselineRef.current = null;
      setInterviewReplyLoading(false);

      return;
    }

    if (lastMessage.event === Interview_Reply_Event.CandidateMessage) {
      if (replyBaselineRef.current === null) {
        replyBaselineRef.current = Math.max(interviewCurrentMessages.length - 1, 0);
      }

      setInterviewReplyLoading(true);

      return;
    }

    const isAssistantMessage =
      lastMessage.event === Interview_Reply_Event.Question ||
      lastMessage.event === Interview_Reply_Event.Followup;

    if (
      isAssistantMessage &&
      (replyBaselineRef.current === null ||
        interviewCurrentMessages.length >= replyBaselineRef.current + 2)
    ) {
      replyBaselineRef.current = null;
      setInterviewReplyLoading(false);
    }
  }, [interviewCurrentMessagesData]);

  useEffect(() => {
    const interviewReply = interviewReplySubscriptionData?.interviewReply;

    if (interviewReply) {
      refetchInterviewCurrentMessages().catch(() => undefined);
    }
  }, [interviewReplySubscriptionData, refetchInterviewCurrentMessages]);

  useEffect(
    () =>
      subscribeToWsReconnect(() => {
        refetchInterviewCurrentMessages().catch(() => undefined);
      }),
    [refetchInterviewCurrentMessages],
  );

  useEffect(() => {
    if (!interviewReplyLoading) {
      return undefined;
    }

    let cancelled = false;
    let timeoutId: number;

    const pollMessages = async () => {
      try {
        await refetchInterviewCurrentMessages();
      } catch {
        // Retry on the next polling interval.
      }

      if (!cancelled) {
        timeoutId = window.setTimeout(pollMessages, REPLY_POLL_INTERVAL);
      }
    };

    timeoutId = window.setTimeout(pollMessages, REPLY_POLL_START_DELAY);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [interviewReplyLoading, refetchInterviewCurrentMessages, sessionId]);

  useEffect(() => {
    if (interviewReplyLoading) {
      stopQuestionTimer();
    } else {
      startQuestionTimer();
    }
  }, [interviewReplyLoading]);

  const loading = interviewCurrentMessagesLoading || interviewReplyLoading;

  const handleSubmitStart = useCallback(() => {
    replyBaselineRef.current = messagesRef.current?.length || 0;
    setInterviewReplyLoading(true);
  }, []);

  const handleSubmitSuccess = useCallback(() => {
    refetchInterviewCurrentMessages().catch(() => undefined);
  }, [refetchInterviewCurrentMessages]);

  const handleSubmitError = useCallback(() => {
    replyBaselineRef.current = null;
    setInterviewReplyLoading(false);
  }, []);

  useEffect(() => {
    if (!isInterviewFinished || finishedHandledRef.current) {
      return;
    }

    finishedHandledRef.current = true;
    showSuccessToast(t('successMessage'));
    handleFinishTesting();
  }, [handleFinishTesting, isInterviewFinished, showSuccessToast, t]);

  return (
    <Stack spacing={2}>
      <QuestionHeader title={t('assessment.question.interview.title')} />
      <Chat
        sessionId={sessionId}
        messages={messagesToPrint}
        loading={loading}
        onSubmitStart={handleSubmitStart}
        onSubmitSuccess={handleSubmitSuccess}
        onSubmitError={handleSubmitError}
      />
    </Stack>
  );
};

export default InterviewQuestion;
